// react
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { url } from '../../services/utils';

/**
 * Clean & Format Raw Product Description into a Unified Text Area Format
 * Decodes mojibake characters, preserves spacing, enters, and structured lines.
 */
export function cleanAndNormalizeDescription(raw) {
    if (!raw) return '';
    let text = String(raw);

    // 1. Recover UTF-8 mojibake
    try {
        text = decodeURIComponent(escape(text));
    } catch (e) {
        text = text
            .replace(/ðŸ”©/g, '🔨')
            .replace(/ðŸ”§/g, '🔧')
            .replace(/âš™ï¸/g, '⚙️')
            .replace(/ðŸ§©/g, '🧩')
            .replace(/ðŸ“¦/g, '📦')
            .replace(/âœ…/g, '✅')
            .replace(/â€“/g, '–')
            .replace(/â€”/g, '—')
            .replace(/â€œ|â€ /g, '"')
            .replace(/â€™/g, "'")
            .replace(/â€¢/g, '•')
            .replace(/Â½/g, '½')
            .replace(/Â¼/g, '¼')
            .replace(/Â¾/g, '¾')
            .replace(/Â/g, '');
    }

    // Fix remaining stray characters
    text = text
        .replace(/â\s*(Keunggulan|Spesifikasi|Fitur)/gi, '⭐ $1')
        .replace(/Â½/g, '½')
        .replace(/Â¼/g, '¼')
        .replace(/Â¾/g, '¾')
        .replace(/â€“/g, '–')
        .replace(/â€”/g, '—')
        .replace(/â/g, '');

    // Normalize unicode spaces and invisible characters (soft hyphens, zero-width spaces, en/em-spaces)
    text = text
        .replace(/[\u00ad\u200b-\u200f\ufeff]/g, '')
        .replace(/[\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]/g, ' ');

    // 2. Remove scraped marketplace boilerplate intros and meta lines
    text = text
        .replace(/^.*Deskripsi\s*Produk\s*\(E[‑-]commerce[^\)]*\)[^\n]*/gim, '')
        .replace(/^.*Berikut\s*ringkasan\s*beberapa\s*contoh:[^\n]*/gim, '')
        .replace(/^.*Dari\s*listing[‑\-\s]*listing\s*di\s*marketplace.*$/gim, '')
        .replace(/^.*Atribut\s*Detail.*$/gim, '');

    // 3. Line-by-line filter for scraped citation artifacts (domains, +count, marketplace names)
    const lines = text.split(/\r?\n/);
    const marketplaceKeywords = [
        'blibli', 'tokopedia', 'shopee', 'lazada', 'bukalapak', 'gocement',
        'olx', 'mitra10', 'depo bangunan', 'monotaro', 'indotrading', 'ralali',
        'juragan material', 'norton bangunan', 'gemilang store', 'bhinneka', 'jakmall',
        'klikmdn', 'kliknclean', 'tokomaterial', 'sumber bangunan', 'cahaya bangunan',
        'anekabangunansby', 'gemilang jaya steel', 'gemilang-jayasteel', 'amariupvc',
        'cahaya bangun perkasa'
    ];

    function isDomainOrUrl(l) {
        if (!l) return false;
        // e.g. amariupvc.com, ibb.jatimprov.go.id, gemilang-jayasteel.co.id
        if (/^(https?:\/\/)?([a-z0-9\-]+\.)+(com|co\.id|go\.id|id|net|org|biz|info|site|online|shop|xyz)(\/[^\s]*)?$/i.test(l)) return true;
        if (/\.(com|co\.id|go\.id|id|net|org)\b/i.test(l) && !l.includes(' ') && l.length < 50) return true;
        return false;
    }

    function isPlusCount(l) {
        // e.g. +1, +2, +3, +4
        return /^\s*\+\s*\d+\s*$/.test(l);
    }

    function isCitationSource(l) {
        const lower = l.toLowerCase();
        if (isDomainOrUrl(l)) return true;
        if (isPlusCount(l)) return true;
        if (marketplaceKeywords.some((kw) => lower === kw || lower.startsWith(`${kw} `) || lower.endsWith(` ${kw}`))) return true;
        // Company name citations like "PT. Cahaya Bangun Perkasa"
        if (/^pt\.?\s+[a-z0-9\s]+$/i.test(l) && l.length < 45 && !l.includes(':')) return true;
        // Standalone brand or acronym tags scraped on a line
        if (/^(am|sni|iso|sgs)$/i.test(lower)) return true;
        return false;
    }

    // Identify and filter out garbage lines
    const isGarbage = new Array(lines.length).fill(false);
    for (let i = 0; i < lines.length; i++) {
        const trimmed = lines[i].trim();
        if (!trimmed) continue;

        if (isCitationSource(trimmed)) {
            isGarbage[i] = true;
        } else if (
            i + 1 < lines.length &&
            isPlusCount(lines[i + 1].trim()) &&
            trimmed.length < 45 &&
            !trimmed.includes(':') &&
            !trimmed.startsWith('-') &&
            !trimmed.startsWith('•')
        ) {
            // Short line immediately preceding a +count (e.g. "Gemilang Store", "Norton Bangunan", "AM")
            isGarbage[i] = true;
        }
    }

    const cleanedLines = [];
    for (let i = 0; i < lines.length; i++) {
        if (isGarbage[i]) continue;

        let line = lines[i];
        // Convert tab-delimited attribute table lines (e.g. "Merek / Produk\tAM 53...") into "Merek / Produk : AM 53..."
        if (line.includes('\t')) {
            const parts = line.split('\t').map((p) => p.trim()).filter(Boolean);
            if (parts.length === 2) {
                line = `${parts[0]} : ${parts[1]}`;
            } else {
                line = line.replace(/\t+/g, ' : ');
            }
        }
        cleanedLines.push(line);
    }

    text = cleanedLines.join('\n');

    // 4. If the text has very few newlines (single wall of text), inject clean enters before key markers
    const newlineCount = (text.match(/\n/g) || []).length;
    if (newlineCount < 3) {
        text = text
            .replace(/\s*(Deskripsi\s*Produk\s*:?)/gi, '\n\n$1\n')
            .replace(/\s*(Spesifikasi\s*Produk\s*:?)/gi, '\n\n✅ $1\n')
            .replace(/\s*(Keunggulan\s*Produk\s*:?)/gi, '\n\n⭐ $1\n')
            .replace(/\s*(Fitur\s*Khusus\s*:?)/gi, '\n$1')
            .replace(/\s*(Sebelum\s*memesan|Catatan\s*:?|Penting\s*:?)/gi, '\n\n$1')
            .replace(/\s*(Admin\s*\d*\s*:)/gi, '\n$1')
            .replace(/\s*(Untuk\s*pengiriman)/gi, '\n$1')
            .replace(/\s*(Happy\s*Shopping)/gi, '\n\n$1')
            .replace(/\s*(Model|Tipe|Type|Bahan|Material|Finishing|Koneksi|Ukuran|Dimensi|Warna|Isi\s*Kemasan|Merk|Brand)\s*:\s*/gi, '\n$1: ');
    }

    // 5. Normalize enters: CRLF to LF, and collapse 3+ enters into max 2
    text = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    text = text.replace(/\n{3,}/g, '\n\n');

    return text.trim();
}

export default function ProductDescription({ product, description }) {
    const [isExpanded, setIsExpanded] = useState(false);

    const rawDesc = description || product?.item_description;
    if (!rawDesc && !product) return null;

    const normalizedText = cleanAndNormalizeDescription(rawDesc);
    const lines = normalizedText.split('\n');
    const isLongDescription = lines.length > 5 || normalizedText.length > 200;

    // Meta Specs derived from product data
    const kondisi = product?.status ? (product.status.toUpperCase() === 'READY' ? 'Baru' : product.status) : 'Baru';
    const berat = product?.berat ? `${product.berat} g` : '500 g';
    const minBeli = `1 ${product?.satuan || 'Buah'}`;
    const kategori = product?.kategori_name;
    const etalase = product?.merek_name || product?.warehouse_name || 'Semua Etalase';

    return (
        <div className="product-desc-tokopedia">
            {/* Meta Specs (Kondisi, Berat Satuan, Min. Beli, Kategori, Etalase) */}
            <div className="product-desc-tokopedia__meta">
                <div className="product-desc-tokopedia__meta-row">
                    <span className="product-desc-tokopedia__meta-label">Kondisi:</span>
                    <span className="product-desc-tokopedia__meta-val">{kondisi}</span>
                </div>
                <div className="product-desc-tokopedia__meta-row">
                    <span className="product-desc-tokopedia__meta-label">Berat Satuan:</span>
                    <span className="product-desc-tokopedia__meta-val">{berat}</span>
                </div>
                <div className="product-desc-tokopedia__meta-row">
                    <span className="product-desc-tokopedia__meta-label">Min. Beli:</span>
                    <span className="product-desc-tokopedia__meta-val">{minBeli}</span>
                </div>
                {kategori && (
                    <div className="product-desc-tokopedia__meta-row">
                        <span className="product-desc-tokopedia__meta-label">Kategori:</span>
                        <Link
                            to={url.category({ id: product?.kategori_id, name: kategori })}
                            className="product-desc-tokopedia__meta-val product-desc-tokopedia__meta-val--link"
                        >
                            {kategori}
                        </Link>
                    </div>
                )}
                {etalase && (
                    <div className="product-desc-tokopedia__meta-row">
                        <span className="product-desc-tokopedia__meta-label">Etalase:</span>
                        <span className="product-desc-tokopedia__meta-val product-desc-tokopedia__meta-val--link">
                            {etalase}
                        </span>
                    </div>
                )}
            </div>

            {/* Description Body with Collapsible State */}
            <div
                className={classNames('product-desc-tokopedia__content', {
                    'product-desc-tokopedia__content--collapsed': isLongDescription && !isExpanded,
                    'product-desc-tokopedia__content--expanded': isLongDescription && isExpanded,
                })}
            >
                {lines.map((line, idx) => {
                    const trimmed = line.trim();

                    if (!trimmed) {
                        return <div key={`line-${idx}`} className="product-desc-tokopedia__spacer" />;
                    }

                    // Section Heading (e.g. "Spesifikasi / Fitur Utama", "Keunggulan:", "⚖ Berat", etc.)
                    const isHeading = /^(deskripsi\s*produk|spesifikasi\s*(\/|barang|produk|teknis)|fitur\s*(utama|khusus)|keunggulan\s*(produk)?|catatan|sebelum\s*memesan|happy\s*shopping|perhatian)/i.test(trimmed) ||
                        (/^[✅⭐📌📦🔧🔨💡🎯✨🚀⚖️]\s*/.test(trimmed) && (trimmed.endsWith(':') || trimmed.length < 35));

                    if (isHeading) {
                        return (
                            <div key={`line-${idx}`} className="product-desc-tokopedia__heading">
                                {trimmed}
                            </div>
                        );
                    }

                    // Admin Phone / WhatsApp line
                    const phoneMatch = trimmed.match(/(admin\s*\d*\s*:?)\s*([0-9\-\s+]+)/i);
                    if (phoneMatch) {
                        const cleanNum = phoneMatch[2].replace(/[^0-9]/g, '');
                        return (
                            <div key={`line-${idx}`} className="product-desc-tokopedia__contact">
                                <span>{phoneMatch[1]} </span>
                                <a
                                    href={`https://wa.me/62${cleanNum.startsWith('0') ? cleanNum.substring(1) : cleanNum}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="product-desc-tokopedia__wa-link"
                                >
                                    💬 {phoneMatch[2].trim()}
                                </a>
                            </div>
                        );
                    }

                    // Key-Value line (e.g. "Model : GAMEPAD SINGLE USB STICK", "BRAND : M-tech", "Ukuran: M6 x 15 mm")
                    const kvMatch = trimmed.match(/^([^:]{2,40})\s*:\s*(.+)$/);
                    if (kvMatch && !trimmed.startsWith('http') && !/^(admin|sebelum|catatan|untuk|happy)/i.test(kvMatch[1])) {
                        return (
                            <div key={`line-${idx}`} className="product-desc-tokopedia__keyvalue">
                                <span className="product-desc-tokopedia__key">{kvMatch[1].trim()} :</span>
                                <span className="product-desc-tokopedia__value"> {kvMatch[2].trim()}</span>
                            </div>
                        );
                    }

                    // Bullet / Feature line
                    const isBullet = /^[•\-\*–]\s*/.test(trimmed) || /^[🔧⚙️🧩📦🔨✅⭐✔💡]\s*/.test(trimmed);
                    if (isBullet) {
                        return (
                            <div key={`line-${idx}`} className="product-desc-tokopedia__bullet">
                                {trimmed}
                            </div>
                        );
                    }

                    // Regular clean paragraph text
                    return (
                        <div key={`line-${idx}`} className="product-desc-tokopedia__text">
                            {trimmed}
                        </div>
                    );
                })}
            </div>

            {/* Toggle Link "Lihat Selengkapnya" */}
            {isLongDescription && (
                <button
                    type="button"
                    className="product-desc-tokopedia__toggle"
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    {isExpanded ? 'Lihat Lebih Sedikit' : 'Lihat Selengkapnya'}
                </button>
            )}

            {/* Bottom Subtle Divider */}
            <div className="product-desc-tokopedia__divider" />
        </div>
    );
}

ProductDescription.propTypes = {
    product: PropTypes.object,
    description: PropTypes.string,
};
